import { Children, PropsWithChildren, ReactNode } from 'react';

import { cn } from '@components/primitives/utilities';

import { NoticeContext, NoticeVariant } from './context';
import { NoticeActions } from './NoticeActions';
import { NoticeDismiss } from './NoticeDismiss';
import { NoticeFooter } from './NoticeFooter';
import { NoticeIcon } from './NoticeIcon';
import { NoticeTitle } from './NoticeTitle';

import css from './styles.module.css';

export interface NoticeProps extends PropsWithChildren {
  className?: string;
  disabled?: boolean;
  variant?: NoticeVariant;
}

/**
 * Wraps bare strings in a paragraph so callers can pass plain text, while
 * leaving element children untouched.
 */
function withParagraphs(children: ReactNode): ReactNode {
  if (typeof children === 'string') return <p>{children}</p>;

  return Children.map(children, (child) =>
    typeof child === 'string' ? <p>{child}</p> : child
  );
}

/**
 * Children render in the order they are written, as direct children of the
 * root. The root is a grid, so the stylesheet places each part by its own
 * class — the notice never rearranges or re-parents what it is given, and DOM
 * order always matches reading order.
 *
 * There is no `align` prop: the stylesheet centres a one-line notice and
 * top-aligns one whose title is followed by body content, so alignment follows
 * the content instead of being set (and mis-set) at the call site.
 */
export function Notice({
  variant = 'info',
  className,
  children,
  disabled,
}: NoticeProps) {
  return (
    <NoticeContext.Provider value={{ variant }}>
      <div
        className={cn(css.notice, className)}
        data-variant={variant}
        data-disabled={disabled}
      >
        {withParagraphs(children)}
      </div>
    </NoticeContext.Provider>
  );
}

Notice.Icon = NoticeIcon;
Notice.Title = NoticeTitle;
Notice.Actions = NoticeActions;
Notice.Footer = NoticeFooter;
Notice.Dismiss = NoticeDismiss;

export type { NoticeVariant } from './context';
export { useNoticeContext } from './context';
export type { NoticeDismissProps } from './NoticeDismiss';
export type { NoticeIconProps } from './NoticeIcon';
export type { NoticeTitleProps } from './NoticeTitle';
